import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import RefreshOutlined from "@mui/icons-material/RefreshOutlined";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  useTheme,
} from "@mui/material";
import {
  Alert,
  Button,
  cellClassName,
  DataTableV2,
} from "@platform/design-system";
import { Invite } from "@platform/types";
import { useSignedInUser } from "@platform/user/client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  JSX,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from "react";
import { useApi } from "@platform/api";
import { AgGridReact } from "ag-grid-react";

import { PAGINATION_PAGE_SIZE_OPTIONS, PENDING } from "../constants";
import { useToastNotificationService } from "../hooks";
import { getStatusColor, extractErrorMessage } from "../utils";
import { submitEvent } from "../utils/tracking";

import ConfirmDialog from "./confirmDialog";

import type { ColDef, ICellRendererParams } from "ag-grid-community";

const errorStateMsg = "Error Viewed";

const DEFAULT_PAGE_SIZE = 10;

const ACTIONS = { RESEND: "RE-SEND", CANCEL: "CANCEL", DELETE: "DELETE" };

interface InvitesTableContext {
  loadingInviteId: number | null;
  onCancel: (invite: Invite) => void;
  onResend: (invite: Invite) => void;
  onDelete: (invite: Invite) => void;
}

const StatusCell = ({ data }: ICellRendererParams<Invite>): ReactElement => {
  const theme = useTheme();
  return (
    <Chip
      sx={{
        borderColor: getStatusColor(theme, data!.status),
        color: getStatusColor(theme, data!.status),
      }}
      label={data!.status}
      variant="outlined"
    />
  );
};

const ActionCell = ({
  data,
  context,
}: ICellRendererParams<Invite, unknown, InvitesTableContext>): ReactElement => {
  const invite = data!;
  if (context.loadingInviteId === invite.id) {
    return <CircularProgress size={20} />;
  }
  return invite.status === PENDING ? (
    <Button
      variant="tertiary"
      onClick={() => context.onCancel(invite)}
      startIcon={<CloseOutlinedIcon />}
    >
      Cancel Invite
    </Button>
  ) : (
    <Button
      variant="tertiary"
      onClick={() => context.onResend(invite)}
      startIcon={<RefreshOutlined />}
    >
      Re-Send Invite
    </Button>
  );
};

const DeleteCell = ({
  data,
  context,
}: ICellRendererParams<Invite, unknown, InvitesTableContext>): ReactElement =>
  context.loadingInviteId === data!.id ? (
    <CircularProgress size={20} />
  ) : (
    <IconButton onClick={() => context.onDelete(data!)}>
      <DeleteOutlineOutlinedIcon />
    </IconButton>
  );

function InvitesTable({
  data,
  brokerId,
  invalidateQueryKey,
}: {
  data: Invite[];
  brokerId: string;
  invalidateQueryKey: string[];
}): JSX.Element {
  const [filteredData, setFilteredData] = useState(data);
  const [error, setError] = useState<string | null>(null);
  // TODO(eslint) convert the hook to TS to fix the lint error
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const { createToastNotification } = useToastNotificationService();
  const [action, setAction] = useState<string | null>(null);
  const [selectedInvite, setSelectedInvite] = useState<Invite | null>(null);
  const { isAdminOrConsultant: adminOrConsultant } = useSignedInUser();
  const [isLoading, setIsLoading] = useState(false);

  const gridRef = useRef<AgGridReact>(null);

  // TODO(eslint): converting the util to TS will make type casting redundant
  const axiosInstance = useApi();
  const queryClient = useQueryClient();

  const { RESEND, CANCEL, DELETE } = ACTIONS;

  useEffect(() => {
    setFilteredData(data);
  }, [data]);

  const { mutate: cancelInvite } = useMutation<void, Error, number>({
    mutationFn: (inviteId: number) =>
      axiosInstance.post("/brokerportal/v1/cancelInvite", {
        inviteId,
      }),
    onSuccess: (_, inviteId) => {
      setFilteredData((prev) =>
        prev.map((i) =>
          i.id === inviteId ? { ...i, status: "Cancelled" } : i,
        ),
      );
      queryClient.invalidateQueries({ queryKey: invalidateQueryKey });
      createToastNotification({
        severity: "success",
        message: "Invite has been cancelled",
      });
    },
    onError: (err) => {
      // TODO(eslint): converting the util to TS will make type casting redundant
      const resolvedError = extractErrorMessage(err) as string;
      submitEvent(errorStateMsg, {
        error_type: "cancel_invite",
        error_reason: resolvedError,
      });
      setError(resolvedError);
    },
    onSettled: () => {
      setIsLoading(false);
      handleClose();
    },
  });

  const { mutate: resendInvite } = useMutation<void, Error, Invite>({
    mutationFn: (invite: Invite) => {
      const { firstName, lastName, email, type } = invite;
      return axiosInstance.post(
        `/brokerportal/v1/${adminOrConsultant ? "inviteUserOrSuperUser" : "inviteAgent"}`,
        {
          firstName,
          lastName,
          email,
          ...(adminOrConsultant && { brokerId, role: type }),
        },
      );
    },
    onSuccess: (_, invite) => {
      setFilteredData((prev) =>
        prev.map((i) =>
          i.email === invite.email ? { ...i, status: "Pending" } : i,
        ),
      );
      queryClient.invalidateQueries({ queryKey: invalidateQueryKey });
      createToastNotification({
        severity: "success",
        message: "User re-invited successfully",
      });
    },
    onError: (e) => {
      // TODO(eslint): converting the util to TS will make type casting redundant
      const resolvedError = extractErrorMessage(e) as string;
      submitEvent(errorStateMsg, {
        error_type: "resend_invite",
        error_reason: resolvedError,
      });
      setError(resolvedError);
    },
    onSettled: () => {
      setIsLoading(false);
      handleClose();
    },
  });

  const { mutate: deleteInviteMutation } = useMutation<void, Error, Invite>({
    mutationFn: (invite: Invite) =>
      axiosInstance.post("/brokerportal/v1/deleteInvite", {
        userEmail: invite.email,
        ...(adminOrConsultant && { brokerId }),
      }),
    onSuccess: (_, invite) => {
      setFilteredData((prev) => prev.filter((i) => i.email !== invite.email));
      queryClient.invalidateQueries({ queryKey: invalidateQueryKey });
      createToastNotification({
        severity: "success",
        message: "Invite has been removed",
      });
    },
    onError: (err) => {
      // TODO(eslint): converting the util to TS will make type casting redundant
      const resolvedError = extractErrorMessage(err) as string;
      submitEvent(errorStateMsg, {
        error_type: "delete_invite",
        error_reason: resolvedError,
      });
      setError(resolvedError);
    },
    onSettled: () => {
      setIsLoading(false);
      handleClose();
    },
  });

  const cancel = (invite: Invite): void => {
    setSelectedInvite(invite);
    setAction(CANCEL);
    setIsLoading(false);
  };

  const reSend = (invite: Invite): void => {
    setSelectedInvite(invite);
    setAction(RESEND);
  };

  const deleteInvite = (invite: Invite): void => {
    setSelectedInvite(invite);
    setAction(DELETE);
  };

  function handleClose(): void {
    setSelectedInvite(null);
    setAction(null);
  }

  const handleCancelInvite = (inviteId: number): void => {
    setIsLoading(true);
    setError(null);
    cancelInvite(inviteId);
  };

  const handleResendInvite = (invite: Invite): void => {
    setIsLoading(true);
    setError(null);
    resendInvite(invite);
  };

  const handleDeleteInvite = (invite: Invite): void => {
    setIsLoading(true);
    setError(null);
    deleteInviteMutation(invite);
  };

  const loadingInviteId =
    isLoading && selectedInvite ? selectedInvite.id : null;

  // AG Grid does not refresh cells when only `context` changes, so force the
  // action/delete cells to re-render when the loading row changes.
  useEffect(() => {
    const api = gridRef.current?.api;
    if (api && !api.isDestroyed()) {
      api.refreshCells({ columns: ["action", "delete"], force: true });
    }
  }, [loadingInviteId]);

  const columns = useMemo<ColDef<Invite>[]>(
    () => [
      {
        flex: 0.75,
        field: "firstName",
        headerName: "First name",
        filter: false,
        suppressHeaderMenuButton: true,
        sort: "asc",
      },
      {
        flex: 1,
        field: "lastName",
        headerName: "Last name",
        filter: false,
        suppressHeaderMenuButton: true,
      },
      {
        flex: 1.75,
        field: "email",
        headerName: "Email",
        filter: false,
        suppressHeaderMenuButton: true,
      },
      {
        flex: 0.5,
        field: "status",
        headerName: "Status",
        filter: false,
        suppressHeaderMenuButton: true,
        cellRenderer: StatusCell,
      },
      {
        flex: 0.75,
        colId: "action",
        filter: false,
        suppressHeaderMenuButton: true,
        cellRenderer: ActionCell,
      },
      {
        flex: 0.25,
        colId: "delete",
        filter: false,
        suppressHeaderMenuButton: true,
        resizable: false,
        cellRenderer: DeleteCell,
      },
    ],
    [],
  );

  const context: InvitesTableContext = {
    loadingInviteId,
    onCancel: cancel,
    onResend: reSend,
    onDelete: deleteInvite,
  };

  return (
    <>
      {!!action && action !== "DELETE" && (
        <ConfirmDialog
          isOpen={!!action}
          title={`${action === CANCEL ? "Cancel" : "Re-send"} Invite`}
          message={`Are you sure you want to ${action === CANCEL ? "cancel" : "re-send"} the invite for ${
            selectedInvite.lastName
          }, ${selectedInvite.firstName} ?`}
          confirmBtnMsg={`YES, ${action} INVITE`}
          onClose={handleClose}
          closeBtnMsg="NO"
          onConfirm={() => {
            if (action === CANCEL) {
              handleCancelInvite(selectedInvite.id);
            } else {
              handleResendInvite(selectedInvite);
            }
          }}
        />
      )}
      {!!action && action === "DELETE" && (
        <ConfirmDialog
          isOpen={!!action}
          title={`Remove Invite`}
          message={`Are you sure you want to remove ${selectedInvite.lastName}, ${selectedInvite.firstName} ?`}
          subMessage={`Note: If this user has an outstanding invitation to the system, it will be cancelled.`}
          confirmBtnMsg={`YES, REMOVE INVITE`}
          onClose={handleClose}
          closeBtnMsg="NO"
          onConfirm={() => {
            handleDeleteInvite(selectedInvite);
          }}
        />
      )}

      {error && (
        <Box mb={2}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}
      <Box
        sx={{
          "& .ag-center-cols-viewport": {
            minHeight:
              filteredData.length === 0
                ? "150px !important"
                : "unset !important",
          }, // AG Grid sets a min height on the viewport that can cause the table to be taller than its content, so unset it when there are rows
          [`& .${cellClassName} .ag-cell-wrapper`]: {
            display: "grid",
            alignItems: "center",
            minHeight: "100%",
          },
        }}
      >
        <DataTableV2
          ref={gridRef}
          rowData={filteredData}
          columnDefs={columns}
          context={context}
          getRowId={(params) => String(params.data.id)}
          overlayNoRowsTemplate="No invites found"
          pagination={true}
          paginationPageSize={DEFAULT_PAGE_SIZE}
          paginationPageSizeSelector={PAGINATION_PAGE_SIZE_OPTIONS}
          autoSizeStrategy={undefined}
        />
      </Box>
    </>
  );
}

export default InvitesTable;
